import { ReactElement, useState, useEffect } from "react";
import { useDependency } from "../../../contexts/DependencyContext";
import { useAsyncCall } from "../../../hooks/UseAsyncCall";
import useWindowWidth from "../../../hooks/UseWindowWidth";
import { className, getColorVariantClassname } from "../../../shared/helpers";
import { IndicatorStatusService } from "../../../services/http/indicatorstatus/IndicatorStatusService";
import { IIndicatorStatus } from "../../../services/http/indicatorstatus/models/IIndicatorStatus";
import { IndicatorStatuses } from "../../../services/http/indicator/models/IndicatorStatuses";
import { densityStorageKey } from "..";
import { ConfigurationUpdate, FiltersUpdate, TableState } from "../table-data";
import CustomSelect from "../../../components/Shared/CustomSelect";
import CustomCheckbox from "../../../components/Shared/CustomCheckbox";
import SearchInput from "./SearchInput";
import FiltersWrapper from "./FiltersWrapper";
import styles from "./Filters.module.scss";

export interface Option
{
    text: string;
    value: any;
}

export enum DensityStates
{
    Expanded = "Expanded",
    Collapsed = "Collapsed"
}

interface IFiltersProps
{
    tableState: TableState,
    setTableFilters: (filters: FiltersUpdate) => void,
    setTableConfiguration: (config: ConfigurationUpdate) => void,
    setClientLoading: (isLoading: boolean) => void;
    isLoading: boolean;
}

function getValueForBitwiseOperation(status: IndicatorStatuses): number
{
    switch (status)
    {
        case IndicatorStatuses.Critical: return 4;
        case IndicatorStatuses.SystemFailure: return 8;
        default: return status;
    }
}

async function loadIndicatorStatuses(service: IndicatorStatusService): Promise<IIndicatorStatus[]>
{
    return await service.getAll();
}

const Filters = (props: IFiltersProps): ReactElement =>
{
    const { filters: parameters, configuration } = props.tableState;
    const service = useDependency(IndicatorStatusService);
    const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);
    const isDesktopOrTablet = useWindowWidth(768);
    const [filterLoading, setFilterLoading] = useState(false);

    // TODO: this gets the indicators on every render, use ref or other means to prevent service calls.
    const { content: indicatorStatuses } = useAsyncCall(loadIndicatorStatuses, service);
    const filterByOptions = indicatorStatuses && indicatorStatuses
        .sort((a, b) => (a.id < b.id ? 1 : -1))
        .map(x => ({ text: x.name, value: x.id } as Option));

    function handleStatusFilter(status: number): void
    {
        setFilterLoading(true);
        setSelectedStatuses(selectedStatuses.includes(status)
            ? selectedStatuses.filter(y => y !== status)
            : [...selectedStatuses, status]
        );
    };

    function handleOfflineFilter(onlyOffline: boolean | undefined): void
    {
        setFilterLoading(true);
        props.setTableFilters({ onlyOffline });
    };

    function handleVisibilityFilter(selectedIndex: number): void
    {
        const visibleSections = configuration.visibleSections.map((x, i) =>
        {
            if (i === selectedIndex) x.visible = !x.visible;
            return x;
        });

        props.setTableConfiguration({ visibleSections });
    };

    function setTableDensity(density: string): void
    {
        localStorage.setItem(densityStorageKey, density);
        props.setTableConfiguration({ density });
    };

    // Handle status filter
    useEffect(() =>
    {
        let flags = 0;

        if (selectedStatuses.length > 0)
        {
            flags = getValueForBitwiseOperation(selectedStatuses[0]);

            for (let i = 1; i < selectedStatuses.length; i++)
                flags |= getValueForBitwiseOperation(selectedStatuses[i]);
        }

        if (parameters && parameters.flags !== flags)
        {
            props.tableState.data.clearRows();
            props.setTableFilters({ flags, pageNumber:  1});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStatuses]);

    useEffect(() => {
        setFilterLoading(false);
    }, [props.isLoading])

    const isLoading = props.isLoading || filterLoading;

    return (
        <section className={styles.mainWrapper}>
            {parameters && (
                <>
                    <SearchInput
                        handleSearch={text => props.setTableFilters({ text, pageNumber: 1 })}
                        isLoading={isLoading}
                        tableState={props.tableState}
                    />

                    <FiltersWrapper>
                        {filterByOptions && (
                            <CustomSelect
                                label="Filter by"
                                customClassName={styles.statusFilter}
                                useHeightTransition={!isDesktopOrTablet}
                            >
                                {filterByOptions.map(x => (
                                    <button
                                        key={x.text}
                                        className={styles.optionWithCheckbox}
                                        onClick={() => handleStatusFilter(x.value)}
                                        disabled={isLoading}
                                    >
                                        <i className="material-icons">
                                            {selectedStatuses.includes(x.value)
                                                ? "check_box"
                                                : "check_box_outline_blank"}
                                        </i>

                                        <span {...className(styles.dot, getColorVariantClassname(x.value))} />
                                        <span>{x.text}</span>
                                    </button>
                                ))}

                                <button
                                    className={styles.clearBtn}
                                    onClick={() => setSelectedStatuses([])}
                                    disabled={isLoading}
                                >
                                    Clear all
                                </button>
                            </CustomSelect>
                        )}

                        <CustomCheckbox
                            label="Offline only"
                            customClassName={styles.offlineCheckbox}
                            handleChange={onlyOffline => handleOfflineFilter(onlyOffline)}
                            disabled={isLoading}
                        />

                        <CustomSelect
                            label="Density"
                            icon="dns"
                            customClassName={styles.densityFilter}
                            useHeightTransition={!isDesktopOrTablet}
                        >
                            {Object.values(DensityStates).map(x => (
                                <button
                                    key={x}
                                    className={configuration.density === x ? "selected" : ""}
                                    onClick={() => setTableDensity(x)}
                                >
                                    {x}
                                </button>
                            ))}
                        </CustomSelect>

                        <CustomSelect
                            label="Visibility"
                            icon="visibility"
                            customClassName={styles.visibilityFilter}
                            useHeightTransition={!isDesktopOrTablet}
                        >
                            {configuration.visibleSections.map((x, i) => (
                                <button
                                    key={x.label}
                                    className={styles.optionWithCheckbox}
                                    onClick={() => handleVisibilityFilter(i)}
                                >
                                    <i className="material-icons">
                                        {x.visible ? "check_box" : "check_box_outline_blank"}
                                    </i>

                                    <span>{x.label}</span>
                                </button>
                            ))}
                        </CustomSelect>
                    </FiltersWrapper>
                </>
            )}
        </section>
    );
};

export default Filters;
